import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdvancedMarker, Map, useMap } from "@vis.gl/react-google-maps";
import { Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import ClusteredDeviceMarker from "./components/ClusteredDeviceMarker";
import ClusteredGatewayMarker from "./components/ClusteredGatewayMarker";
import moment from "moment/moment";

const GoogleMapBoardComponent = React.memo(
  ({ devices, deviceType, selectedRow, onSelectRow, resetSelectedeDevice }) => {
    const MarkerComponent = useMemo(() => {
      const commonProps = {
        devices,
        deviceType,
        selectedRow,
        onSelectRow,
        resetSelectedeDevice,
      };

      let component = null;

      switch (deviceType) {
        case 4:
          component = <ClusteredLevelMonitorDeviceMarker {...commonProps} />
          break;
        case 9:
          component = <ClusteredGatewayMarker {...commonProps} />
          break;
        default:
          component = <ClusteredDeviceMarker {...commonProps} />
          break;
      }

      return component;

    }, [devices, deviceType, selectedRow, onSelectRow, resetSelectedeDevice]);

    return (
      <MDBox style={{ width: "100%", height: "600px" }}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={{ lat: 0, lng: 0 }}
          defaultZoom={2}
          gestureHandling="greedy"
          mapId="d089c1a7f8831a36"
        >
          {MarkerComponent}
        </Map>
      </MDBox>
    );
  },
  // custom comparison function to prevent re-rendering if props are the same
  (prevProps, nextProps) =>
    prevProps.deviceType === nextProps.deviceType &&
    prevProps.selectedRow === nextProps.selectedRow &&
    prevProps.onSelectRow === nextProps.onSelectRow &&
    prevProps.resetSelectedeDevice === nextProps.resetSelectedeDevice &&
    JSON.stringify(prevProps.devices) === JSON.stringify(nextProps.devices)
);

const ClusteredLevelMonitorDeviceMarker = ({ devices = [], deviceType, selectedRow, onSelectRow, resetSelectedeDevice }) => {
  const [markers, setMarkers] = useState({});
  const [selectedTreeKey, setSelectedTreeKey] = useState(null);
  const zoomRef = useRef(null);
  const map = useMap();
  const clustererRef = useRef(null);
  const [deviceData, setDeviceData] = useState([]);

  // Initialize map and clusterer
  useEffect(() => {
    setDeviceData([])
    if (!map) return;
    // clustererRef.current = null
    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({ map, algorithm: new SuperClusterAlgorithm({ maxZoom: 20 }) });
    }
    let deviceMap = new Set();
    if (devices.length >= 1) {
      setSelectedTreeKey(null);
      onSelectRow(null);
    }
    if (devices.length > 0) {
      // setSelectedTreeKey(null)
      const bounds = new google.maps.LatLngBounds();
      devices.forEach((device) => {
        const { lat, lng } = device;
        if (
          typeof lat === 'number' && typeof lng === 'number' &&
          lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
        ) {
          bounds.extend({ lat, lng });
        } else {
          console.warn('Invalid coordinates:', device);
        }
      });
      setTimeout(() => {
        map.fitBounds(bounds);
      }, 200); // adjust delay as needed
      let newDevice = devices.map((device) => {
        let lat = parseFloat(device.lat);
        let lng = parseFloat(device.lng);

        // Validate coordinates before proceeding
        const isValidLat = typeof lat === 'number' && !isNaN(lat);
        const isValidLng = typeof lng === 'number' && !isNaN(lng);

        // If either coordinate is invalid, skip position or set default
        if (!isValidLat || !isValidLng) {
          console.warn(`Invalid lat/lng for device ID ${device.id}`, device.lat, device.lng);
          return {
            id: device.id,
            device: {
              ...device,
              iconClassName: device.iconClassName || "fas fa-map-pin",
              color: device.color || "#000",
              text: device.text || "",
            },
            position: null, // or undefined, or skip this device entirely
          };
        }

        // Make lng unique if needed
        while (deviceMap.has(lng)) {
          lng = parseFloat((lng + 0.000050).toFixed(6));
        }

        deviceMap.add(lng);

        return {
          id: device.id,
          device: {
            ...device,
            iconClassName: device.iconClassName || "fas fa-map-pin",
            color: device.color || "#000",
            text: device.text || "",
          },
          position: { lat, lng }
        };
      });
      setDeviceData(newDevice)
    }
    // setMarkers({})

  }, [map, devices]);

  // Update clusterer when markers change
  useEffect(() => {
    if (!clustererRef.current || !map) return;

    clustererRef.current.clearMarkers();
    const markerValues = Object.values(markers).filter(Boolean);
    if (markerValues.length > 0) {
      clustererRef.current.addMarkers(markerValues);
    }
  }, [markers, map]);

  // Handle selected device
  const selectedDevice = useMemo(
    () => devices.find((t) => t.id === selectedTreeKey) || null,
    [devices, selectedTreeKey]
  );

  // Center map on selected device
  useEffect(() => {
    if (selectedDevice && map) {
      const zoomLevel = map.getZoom();
      zoomRef.current = zoomLevel;
      const position = {
        lat: selectedDevice.lat,
        lng: selectedDevice.lng,
      };
      map.setCenter(position);
      map.setZoom(20);
    }
  }, [selectedDevice, map]);

  useEffect(() => {
    if (deviceType) {
      zoomRef.current = null;
      setSelectedTreeKey(null)
      map.setZoom(12);
    }
  }, [deviceType]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((prevMarkers) => {
      if (!marker) {
        const { [key]: _, ...newMarkers } = prevMarkers;
        return newMarkers;
      }
      return { ...prevMarkers, [key]: marker };
    });
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    resetSelectedeDevice()
    setSelectedTreeKey(null);
    onSelectRow(null);
    if (zoomRef.current && map) {
      map.setZoom(zoomRef.current);
    }
  }, [map, selectedRow]);

  const handleMarkerClick = useCallback((tree) => {
    setSelectedTreeKey(tree.id);
  }, []);

  useEffect(() => {
    if (selectedRow) {
      handleMarkerClick(selectedRow)
    }
  }, [selectedRow])

  const formatAddress = (address) =>
    [
      address?.street_address1,
      address?.street_address2,
      address?.city,
      address?.state,
      address?.postal_code,
      address?.country,
    ]
      .filter(Boolean)
      .join(", ");

  return (
    <>
      {map && deviceData.map(({ id, device, position }) => (
        <AdvancedMarkerWithRefLM
          key={id}
          device={device}
          position={position}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}
      {selectedDevice && (
        <Card
          sx={{
            position: "absolute",
            bottom: 16,
            left: 16,
            padding: 2,
            backgroundColor: "white",
          }}
        >
          <MDTypography sx={{ fontSize: "20px" }}>
            {selectedDevice.attributes.device_data.name} #{selectedDevice.attributes.device_id}
          </MDTypography>
          <MDTypography sx={{ fontSize: "15px" }}>
            Address: {formatAddress(selectedDevice.attributes?.entity_address_item)}
          </MDTypography>
          <CardView deviceValue={selectedDevice.attributes?.device_value} />
          <MDButton color="dark" onClick={() => handleInfoWindowClose()}>
            Close
          </MDButton>
        </Card>
      )}
    </>
  );
};

const AdvancedMarkerWithRefLM = ({ device, position, onClick, setMarkerRef }) => {
  const handleClick = useCallback(() => onClick(device), [onClick, device]);
  const ref = useCallback(
    (marker) => {
      setMarkerRef(marker, device.id);
    },
    [setMarkerRef, device.id]
  );

  // Validate position
  if (!position || typeof position.lat !== "number" || typeof position.lng !== "number") {
    console.warn(`Invalid position for device ${device.id}:`, position);
    return null;
  }

  const RenderIcon =
    () => {
      const iconStyle = {
        color: device.color || "#000",
      };

      return (
        <div className="icon-wrapper" style={iconStyle} title={device.title ?? ""}>
          <h6
            style={{
              position: "absolute",
              top: "-17px",
              color: "#000",
              whiteSpace: "nowrap",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {device.text || ""}
          </h6>
          <i
            className={device.iconClassName || "fas fa-map-pin"}
            style={{ fontSize: "24px" }}
          ></i>
        </div>
      );
    }

  return (
    <AdvancedMarker position={position} ref={ref} onClick={handleClick}>
      <RenderIcon />
    </AdvancedMarker>
  );
};

function CardView({ deviceValue }) {
  return (
    <>
      <MDTypography sx={{ fontSize: "15px" }}>
        Inch level: {deviceValue?.device_data?.inch_level}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Volume level: {deviceValue?.device_data?.volume_level}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Percent level: {deviceValue?.device_data?.percent_level}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Battery Status: {deviceValue?.device_data?.battery_status}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Battery Voltage: {deviceValue?.device_data?.battery_voltage}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Wifi Signal: {deviceValue?.device_data?.wifi_signal}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Tx Reported: {deviceValue?.tx_reported}
      </MDTypography>
      <MDTypography sx={{ fontSize: "15px" }}>
        Last Updated on: {
          deviceValue?.last_updated_on ??
            moment(deviceValue?.last_device_updated).isValid() ? moment(deviceValue?.last_device_updated).fromNow() : ''
        }
      </MDTypography>
    </>
  )
}

export default GoogleMapBoardComponent;